import { Injectable, OnDestroy, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  tap,
  catchError,
  switchMap,
  throwError,
  shareReplay,
  finalize,
  firstValueFrom,
} from 'rxjs';
import { environment } from '@/environments/environment';
import { AUTH_ENDPOINTS, STORAGE_KEYS } from '@/app/core/const/auth.const';
import {
  SignInRequest,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  User,
  AuthState,
} from '@/app/core/models/auth.types';
import { LocalStorageService } from './localStorage.service';
import type { NotificationService } from './notification.service';

/** Lee de `sessionStorage` sin romper donde no hay (modo privado, sandbox). */
function leerDeLaPestana(clave: string): string | null {
  try {
    return sessionStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribirEnLaPestana(clave: string, valor: string): void {
  try {
    sessionStorage.setItem(clave, valor);
  } catch {
    // Sin sessionStorage la sesión no se puede acotar a la pestaña: queda en memoria y se va
    // al recargar, que es lo más cerca de «no recordarme» que el navegador permite.
  }
}

function borrarDeLaPestana(clave: string): void {
  try {
    sessionStorage.removeItem(clave);
  } catch {
    // Nada que borrar donde no hay sessionStorage.
  }
}

/**
 * Whether a failure ends the session: a 401 or a 403 (the server judged it), or a failure that is
 * not HTTP at all (no refresh token stored). No network, a 5xx, a 429, a 402… mean the server could
 * not answer or did not judge the session, and a session nobody judged is still a session.
 */
export function endsTheSession(err: unknown): boolean {
  return !(err instanceof HttpErrorResponse) || err.status === 401 || err.status === 403;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private authState$ = new BehaviorSubject<AuthState>(this.getInitialState());
  public readonly currentUser = signal<User | null>(null);

  // Shared in-flight token refresh. Concurrent 401s subscribe to this same
  // Observable instead of each firing their own /auth/refresh and racing the
  // rotating (single-use) refresh token.
  private refreshInFlight$: Observable<RefreshTokenResponse> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private accessTokenExpiresAtMs: number | null = null;
  private accessTokenLifetimeMs: number | null = null;
  private readonly visibilityChangeHandler = (): void => {
    this.refreshWhenReturningToForeground();
  };

  /**
   * «Recordarme» sin marcar: la sesión vive sólo en esta pestaña.
   *
   * La casilla del ingreso estaba dibujada en la v1 y no hacía nada —el formulario la validaba y
   * llamaba a `signInAndFetchUser` sin ella—, y la v2 la sacó por eso. Vuelve porque el mockup la
   * dibuja, y ahora hace lo que dice: marcada (lo de siempre), los tokens van a `localStorage` y
   * la sesión sobrevive a cerrar el navegador; sin marcar, van a `sessionStorage` y la sesión se
   * termina al cerrar la pestaña. Es del navegador y no del servidor: el refresh token dura lo
   * mismo en los dos casos, lo que cambia es si este navegador lo guarda.
   */
  private soloEstaPestana = false;

  constructor(
    private readonly http: HttpClient,
    private readonly storage: LocalStorageService,
  ) {
    this.initializeAuthState();
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    this.cancelScheduledRefresh();
  }

  /**
   * Initialize authentication state from localStorage.
   * In development, if environment.devToken is set and no token exists,
   * auto-inject the mock token so the local backend can be used without a real sign-in flow.
   */
  private initializeAuthState(): void {
    if (
      !environment.production &&
      environment.devToken &&
      !this.storage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    ) {
      this.storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, environment.devToken);
      this.storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, environment.devToken);
    }

    this.soloEstaPestana = leerDeLaPestana(STORAGE_KEYS.ACCESS_TOKEN) !== null;
    const token = this.storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);

    if (token) {
      this.authState$.next({
        ...this.authState$.value,
        token,
        // isAuthenticated stays false until verifyToken() succeeds via authGuard
      });
      this.restoreTokenTiming(token);
    }
  }

  /**
   * Get initial authentication state
   */
  private getInitialState(): AuthState {
    return {
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      loading: false,
      error: null,
    };
  }

  /**
   * Get authentication state observable
   */
  public getAuthState$(): Observable<AuthState> {
    return this.authState$.asObservable();
  }

  /**
   * Get current authentication state
   */
  public getAuthState(): AuthState {
    return this.authState$.value;
  }

  /**
   * Sign in with email and password. `recordar` es la casilla «Recordarme» del ingreso: sin
   * marcar, la sesión queda en esta pestaña (ver `soloEstaPestana`).
   */
  public signIn(email: string, password: string, recordar = true): Observable<SignInResponse> {
    const payload: SignInRequest = { email, password };

    return this.http
      .post<SignInResponse>(`${environment.apiUrl}${AUTH_ENDPOINTS.SIGN_IN}`, payload)
      .pipe(
        tap((response) => {
          // Save authentication data (tokens and user ID)
          this.saveAuthData(response, recordar);
        }),
        catchError((error) => {
          const errorMessage = error?.error?.detail || 'Error de autenticación';
          this.authState$.next({
            ...this.authState$.value,
            error: errorMessage,
          });
          throw error;
        }),
      );
  }

  /**
   * Complete authentication flow: sign in and fetch user details
   * This method should be called instead of signIn() when you need full user data
   *
   * `switchMap` takes the promise of `verifyToken` as it is: it emits the user and completes, or
   * fails with the failure of `/auth/me`. The `Observable` written by hand that wrapped it did the
   * same and also logged the failure to the console, which whoever subscribes already handles.
   */
  public signInAndFetchUser(email: string, password: string, recordar = true): Observable<User> {
    return this.signIn(email, password, recordar).pipe(switchMap(() => this.verifyToken()));
  }

  /**
   * Sign up with email, password and company info
   */
  public signUp(
    email: string,
    password: string,
    companyName: string,
    country: string,
  ): Observable<SignUpResponse> {
    const payload: SignUpRequest = {
      email,
      password,
      company_name: companyName,
      country,
    };

    return this.http
      .post<SignUpResponse>(`${environment.apiUrl}${AUTH_ENDPOINTS.SIGN_UP}`, payload)
      .pipe(
        catchError((error) => {
          const errorMessage = error?.error?.detail || 'Error en el registro';
          this.authState$.next({
            ...this.authState$.value,
            error: errorMessage,
          });
          throw error;
        }),
      );
  }

  public async verifyToken(): Promise<User> {
    try {
      const response = await this.http
        .get<User>(`${environment.apiUrl}${AUTH_ENDPOINTS.ME}`)
        .toPromise();

      if (!response) {
        throw new Error('No response from server');
      }

      this.authState$.next({
        ...this.authState$.value,
        user: response,
        isAuthenticated: true,
        loading: false,
      });
      this.currentUser.set(response);

      return response;
    } catch (err) {
      this.authState$.next({
        ...this.authState$.value,
        loading: false,
        error: 'Error al verificar token',
      });
      throw err;
    }
  }

  /**
   * Checks the stored session against `/auth/me` and, when the server rejects the access token,
   * renews it once with the refresh token before giving up. That is «Recordarme» the morning after:
   * the access token expired overnight, the refresh token did not. The refresh is the shared one
   * (`refreshAccessToken`): the one `restoreTokenTiming` starts on its own for an expired token is
   * the same request, and the single-use refresh token is not burnt twice.
   *
   * A 404 of `/auth/me` is judged like a 401: «User not found» or «Tenant not found», the person or
   * the account deleted while the session lived on in Redis (its revocation is best-effort). The
   * refresh tries once —for a deleted user it answers 401 (H104)— and a 404 again after renewing ends
   * the session too. Until 10-17 develop sent that person to sign-in; retrying it forever with «tu
   * sesión sigue abierta» was the regression the review measured.
   *
   * `true`: the session is valid and the user is loaded. `false`: it ended ({@link endsTheSession})
   * and the refresh could not renew it. Any other failure REJECTS with that failure, of `/auth/me`
   * or of the refresh: whoever asked keeps the session and asks again.
   */
  public async restoreSession(): Promise<boolean> {
    for (let renewed = 0; ; renewed++) {
      const sent = this.getToken();
      try {
        await this.verifyToken();
        return true;
      } catch (err) {
        if (!endsTheSession(err) && (err as HttpErrorResponse).status !== 404) throw err;
        if (renewed) return false;
        // The refresh `restoreTokenTiming` started on its own may have finished while `/auth/me`
        // was in flight: the pair is already new, so it asks again with it instead of rotating it
        // a second time. That rotation counts as the one renewal.
        if (this.getToken() !== sent) continue;
        try {
          await firstValueFrom(this.refreshAccessToken());
        } catch (refreshErr) {
          if (!endsTheSession(refreshErr)) throw refreshErr;
          return false;
        }
      }
    }
  }

  /**
   * Save authentication data to localStorage and update state
   */
  private saveAuthData(response: SignInResponse, recordar = true): void {
    this.soloEstaPestana = !recordar;
    this.guardarTokens(response.access_token, response.refresh_token);

    this.authState$.next({
      isAuthenticated: true,
      user: null, // Will be populated after verifyToken() is called
      token: response.access_token,
      refreshToken: response.refresh_token,
      loading: false,
      error: null,
    });
    this.scheduleRefresh(response.expires_in);
  }

  /**
   * Refresh the access token.
   *
   * Single-flight: while a refresh is in progress, concurrent callers get the
   * same shared Observable, so only one POST /auth/refresh is issued. When no
   * refresh token is stored the Observable errors (the caller treats that as
   * an unrecoverable session) instead of completing silently.
   */
  /**
   * Adopt a token pair the backend rotated for us, keeping the session alive.
   *
   * Changing the password kills every live session, including this browser's.
   * The backend hands back a fresh pair for exactly that reason — but writing
   * it straight to `localStorage` is NOT enough, and that is precisely the bug
   * this method exists to prevent: the interceptor signs every request with
   * `getToken()`, which returns the IN-MEMORY token, so a caller that only
   * touched storage kept using the token the password change had just killed.
   * One click later: 401, a refresh that fails for the same reason,
   * `clearSession()` wiping the fresh pair out of storage, and the user thrown
   * to sign-in right after doing everything correctly.
   *
   * `user` and `isAuthenticated` carry over on purpose: this rotates the
   * credential, it does not re-authenticate, so dropping the loaded profile
   * would blank the UI for no reason.
   */
  public adoptRotatedTokens(accessToken: string, refreshToken: string): void {
    this.guardarTokens(accessToken, refreshToken);

    this.authState$.next({
      ...this.authState$.value,
      isAuthenticated: true,
      token: accessToken,
      refreshToken,
    });
  }

  public refreshAccessToken(): Observable<RefreshTokenResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const payload: RefreshTokenRequest = { refresh_token: refreshToken };

    this.refreshInFlight$ = this.http
      .post<RefreshTokenResponse>(`${environment.apiUrl}${AUTH_ENDPOINTS.REFRESH}`, payload)
      .pipe(
        tap((response) => {
          this.guardarTokens(response.access_token, response.refresh_token);

          this.authState$.next({
            ...this.authState$.value,
            token: response.access_token,
            refreshToken: response.refresh_token,
          });
          this.scheduleRefresh(response.expires_in);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshInFlight$;
  }

  /**
   * Sign out: tear the session down locally right away, then tell the backend.
   * The local teardown is synchronous so it happens even if the caller never
   * subscribes to the returned Observable.
   *
   * **La falla del servidor ya no se traga.** Antes el aviso era «best-effort»
   * con un `catchError(() => of(undefined))`: si el servidor no confirmaba, quien
   * llamaba recibía un éxito y mandaba a la persona a la pantalla de ingreso como
   * si todo hubiera salido, con la sesión posiblemente abierta del lado del
   * servidor y nada en pantalla que lo dijera (el barrido de los guardados de la
   * 1.7 lo midió en el shell). Ahora el Observable falla, y quien llama decide cómo
   * decirlo; la sesión LOCAL se cierra igual, pase lo que pase.
   */
  public signOut(): Observable<void> {
    const token = this.getToken();
    this.clearSession();
    return this.avisarElCierre(token);
  }

  /**
   * Le avisa al servidor que la sesión de ESE token se cerró.
   *
   * Está separado de `signOut` para que el cierre que el servidor no confirmó se
   * pueda reintentar: después de `clearSession` el token ya no está en ningún
   * lado más que en quien lo guardó antes de cerrar.
   */
  public avisarElCierre(token: string | null): Observable<void> {
    const options = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    return this.http.post<void>(`${environment.apiUrl}${AUTH_ENDPOINTS.SIGN_OUT}`, {}, options);
  }

  /**
   * Tear down the local session synchronously: drop the in-flight refresh,
   * clear stored tokens and reset auth state. Idempotent — safe to call from
   * the interceptor/guard without subscribing to anything.
   */
  public clearSession(): void {
    this.cancelScheduledRefresh();
    this.accessTokenExpiresAtMs = null;
    this.accessTokenLifetimeMs = null;
    this.refreshInFlight$ = null;
    this.storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    this.storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    this.storage.removeItem(STORAGE_KEYS.USER_DATA);
    borrarDeLaPestana(STORAGE_KEYS.ACCESS_TOKEN);
    borrarDeLaPestana(STORAGE_KEYS.REFRESH_TOKEN);
    this.soloEstaPestana = false;

    this.authState$.next(this.getInitialState());
    this.currentUser.set(null);
  }

  /**
   * Clears a session the server rejected and says so, once: concurrent 401s find it already cleared
   * and say nothing. Returns whether there was a session to clear, so that whoever calls sends the
   * person to sign-in only once too.
   */
  public expireSession(notification: Pick<NotificationService, 'error'>): boolean {
    const hadSession = this.getToken() !== null;
    this.clearSession();
    if (hadSession) {
      notification.error('Tu sesión expiró. Inicia sesión de nuevo.', 'Sesión expirada');
    }
    return hadSession;
  }

  /**
   * Guarda el par de tokens donde corresponde: en `localStorage` si la sesión se recuerda, en
   * `sessionStorage` si vive sólo en esta pestaña. Y lo borra del otro lado, para que un par
   * viejo no sobreviva a la rotación en el almacén que ya no se usa.
   */
  private guardarTokens(acceso: string, refresco: string): void {
    if (this.soloEstaPestana) {
      escribirEnLaPestana(STORAGE_KEYS.ACCESS_TOKEN, acceso);
      escribirEnLaPestana(STORAGE_KEYS.REFRESH_TOKEN, refresco);
      this.storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      this.storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      return;
    }
    this.storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, acceso);
    this.storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresco);
    borrarDeLaPestana(STORAGE_KEYS.ACCESS_TOKEN);
    borrarDeLaPestana(STORAGE_KEYS.REFRESH_TOKEN);
  }

  private scheduleRefresh(expiresInSeconds: number): void {
    if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
      return;
    }

    const lifetimeMs = expiresInSeconds * 1000;
    this.accessTokenLifetimeMs = lifetimeMs;
    this.accessTokenExpiresAtMs = Date.now() + lifetimeMs;
    this.scheduleRefreshForCurrentToken();
  }

  private scheduleRefreshForCurrentToken(): void {
    this.cancelScheduledRefresh();
    if (this.accessTokenExpiresAtMs === null || this.accessTokenLifetimeMs === null) {
      return;
    }

    const refreshAtMs = this.accessTokenExpiresAtMs - this.accessTokenLifetimeMs * 0.25;
    const delayMs = Math.max(0, refreshAtMs - Date.now());
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.runPreventiveRefresh();
    }, delayMs);
  }

  private cancelScheduledRefresh(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private runPreventiveRefresh(): void {
    this.refreshAccessToken().subscribe({
      // A temporary network failure must not sign the user out. The reactive
      // interceptor remains the final recovery path for subsequent requests.
      error: () => undefined,
    });
  }

  private refreshWhenReturningToForeground(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (this.accessTokenExpiresAtMs === null || this.accessTokenLifetimeMs === null) {
      const token = this.storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        this.restoreTokenTiming(token);
      }
    }

    if (this.accessTokenExpiresAtMs === null || this.accessTokenLifetimeMs === null) {
      return;
    }

    const remainingMs = this.accessTokenExpiresAtMs - Date.now();
    if (remainingMs <= this.accessTokenLifetimeMs * 0.25) {
      this.cancelScheduledRefresh();
      this.runPreventiveRefresh();
    }
  }

  private restoreTokenTiming(token: string): void {
    try {
      const payloadSegment = token.split('.')[1];
      if (!payloadSegment) {
        return;
      }

      const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(paddedBase64)) as { exp?: number; iat?: number };
      if (typeof payload.exp !== 'number') {
        return;
      }

      const expiresAtMs = payload.exp * 1000;
      const lifetimeMs =
        typeof payload.iat === 'number'
          ? (payload.exp - payload.iat) * 1000
          : expiresAtMs - Date.now();
      if (lifetimeMs <= 0) {
        return;
      }

      this.accessTokenExpiresAtMs = expiresAtMs;
      this.accessTokenLifetimeMs = lifetimeMs;
      this.scheduleRefreshForCurrentToken();
    } catch {
      // Locally configured development tokens may not be JWTs.
    }
  }

  /**
   * Update the current user signal (e.g. after profile edits)
   */
  public updateCurrentUser(partial: Partial<User>): void {
    const current = this.currentUser();
    if (!current) return;
    const updated = { ...current, ...partial };
    this.currentUser.set(updated);
    this.authState$.next({ ...this.authState$.value, user: updated });
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.authState$.value.isAuthenticated;
  }

  /**
   * Get current authentication token
   */
  public getToken(): string | null {
    return this.authState$.value.token;
  }

  /**
   * Get current user
   */
  public getUser(): User | null {
    return this.authState$.value.user;
  }

  /**
   * Per-tenant add-ons currently enabled for the user's tenant (RTA-01).
   */
  public getEnabledFeatures(): string[] {
    return this.authState$.value.user?.enabled_features ?? [];
  }

  /**
   * True if the current tenant has the given add-on enabled.
   */
  public hasFeature(slug: string): boolean {
    return this.getEnabledFeatures().includes(slug);
  }
}
